import React, { useState } from 'react';
import RISLogo from '../assets/RIS-LoGO.png';
import { useAuth } from '../auth/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Loader2, ArrowLeft, KeyRound, CheckCircle2 } from 'lucide-react';

const LoginScreen = () => {
    const { loginDirect, resetPassword, confirmResetPassword, confirmNewPassword, loading: authLoading } = useAuth();

    // View State: 'login' | 'forgot-password' | 'reset-password' | 'force-change-password'
    const [view, setView] = useState('login');

    // Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // UI State
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const resetStates = () => {
        setError('');
        setSuccess('');
        setIsLoading(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        resetStates();
        setIsLoading(true);

        if (!email || !password) {
            setError('Please enter both email and password.');
            setIsLoading(false);
            return;
        }

        const result = await loginDirect(email, password);
        if (result.success) {
            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setView('force-change-password');
                setSuccess('Please set a new secure password.');
                setIsLoading(false);
                return;
            }
        } else {
            const errorCode = result.error?.name || result.error?.code;
            let message = typeof result.error === 'string' ? result.error : 'Login failed. Please check your credentials.';
            if (errorCode === 'UserNotFoundException') message = 'User does not exist.';
            else if (errorCode === 'NotAuthorizedException') message = 'Incorrect email or password.';
            else if (errorCode === 'UserNotConfirmedException') message = 'User account not confirmed.';
            setError(message);
            alert("Login Error: " + message); // Explicitly added to guarantee visibility
        }
        setIsLoading(false);
    };

    const handleForceConfirm = async (e) => {
        e.preventDefault();
        resetStates();
        setIsLoading(true);

        if (!newPassword || newPassword !== confirmPassword) {
            setError('Passwords do not match or are empty.');
            setIsLoading(false);
            return;
        }

        const result = await confirmNewPassword(newPassword);
        if (!result.success) {
            setError(typeof result.error === 'string' ? result.error : 'Failed to save new password.');
        }
        setIsLoading(false);
    };

    const handleRequestReset = async (e) => {
        e.preventDefault();
        resetStates();
        setIsLoading(true);

        if (!email) {
            setError('Please enter your corporate email.');
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(email);
            setSuccess('Verification code sent to your email.');
            setView('reset-password');
        } catch (err) {
            setError(err.message || 'Failed to send reset code.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmReset = async (e) => {
        e.preventDefault();
        resetStates();
        setIsLoading(true);

        if (!code || !newPassword) {
            setError('Please enter the code and your new password.');
            setIsLoading(false);
            return;
        }

        try {
            await confirmResetPassword(email, code, newPassword);
            setSuccess('Password reset successful! You can now login.');
            setView('login');
            setPassword(''); // Clear old password
        } catch (err) {
            setError(err.message || 'Failed to reset password.');
        } finally {
            setIsLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '0.65rem 1rem 0.65rem 2.5rem',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '0.9rem',
        outline: 'none',
        transition: 'all 0.2s',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box' // Fixes the width exceeding the container
    };

    const iconStyle = {
        position: 'absolute',
        left: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8'
    };

    const cardStyle = {
        backgroundColor: 'white',
        padding: '2.5rem 2rem',
        borderRadius: '20px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '360px', // More compact as requested
        width: '90%',
        position: 'relative'
    };

    const buttonStyle = {
        width: '100%',
        padding: '0.7rem 1.5rem',
        backgroundColor: '#0f172a',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '0.9rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        marginTop: '0.5rem',
        boxSizing: 'border-box'
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'center',
            alignItems: 'center', flexDirection: 'column', zIndex: 50, fontFamily: 'sans-serif'
        }}>
            <div style={cardStyle}>
                {view !== 'login' && (
                    <button
                        onClick={() => { setView('login'); resetStates(); }}
                        style={{
                            position: 'absolute', left: '1.5rem', top: '1.5rem',
                            background: 'none', border: 'none', color: '#64748b',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.8rem', fontWeight: 500
                        }}
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                )}

                <img src={RISLogo} alt="RIS Logo" style={{ width: '100px', height: 'auto', marginBottom: '1.25rem' }} />

                <h1 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.4rem', textAlign: 'center' }}>
                    {view === 'login' ? 'Task Management System' : view === 'forgot-password' ? 'Forgot Password?' : view === 'force-change-password' ? 'Update Password' : 'Reset Password'}
                </h1>

                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1.75rem', textAlign: 'center', maxWidth: '240px' }}>
                    {view === 'login' ? 'Please sign in to continue' : view === 'forgot-password' ? 'Enter your email to receive a reset code' : view === 'force-change-password' ? 'Please set a new secure password before continuing.' : 'Enter the code sent to your email'}
                </p>

                {(error || success) && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem',
                        backgroundColor: error ? '#fee2e2' : '#f0fdf4',
                        color: error ? '#dc2626' : '#16a34a',
                        borderRadius: '8px', fontSize: '0.75rem', marginBottom: '1rem', width: '100%'
                    }}>
                        {error ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
                        {error || success}
                    </div>
                )}

                {view === 'login' && (
                    <form onSubmit={handleLogin} style={{ width: '100%' }}>
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                            <Mail size={16} style={iconStyle} />
                            <input
                                type="email" placeholder="Organization Email" value={email}
                                onChange={(e) => setEmail(e.target.value)} style={inputStyle}
                                autoComplete="username"
                            />
                        </div>
                        <div style={{ marginBottom: '0.5rem', position: 'relative' }}>
                            <Lock size={16} style={iconStyle} />
                            <input
                                type={showPassword ? "text" : "password"} placeholder="Password"
                                value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle}
                                autoComplete="current-password"
                            />
                            <button
                                type="button" onClick={() => setShowPassword(!showPassword)}
                                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                            >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>

                        <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
                            <button
                                type="button"
                                onClick={() => { setView('forgot-password'); resetStates(); }}
                                style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer' }}
                            >
                                Forgot Password?
                            </button>
                        </div>

                        <button type="submit" disabled={isLoading || authLoading} style={{ ...buttonStyle, opacity: (isLoading || authLoading) ? 0.7 : 1 }}>
                            {(isLoading || authLoading) ? <Loader2 size={16} className="animate-spin" /> : 'Login'}
                        </button>
                    </form>
                )}

                {view === 'forgot-password' && (
                    <form onSubmit={handleRequestReset} style={{ width: '100%' }}>
                        <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
                            <Mail size={16} style={iconStyle} />
                            <input
                                type="email" placeholder="Enter your email" value={email}
                                onChange={(e) => setEmail(e.target.value)} style={inputStyle}
                                autoComplete="username"
                            />
                        </div>
                        <button type="submit" disabled={isLoading} style={buttonStyle}>
                            {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Send Reset Code'}
                        </button>
                    </form>
                )}

                {view === 'reset-password' && (
                    <form onSubmit={handleConfirmReset} style={{ width: '100%' }}>
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                            <Mail size={16} style={iconStyle} />
                            <input
                                type="email" placeholder="Organization Email" value={email}
                                readOnly style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                                autoComplete="username"
                            />
                        </div>
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                            <KeyRound size={16} style={iconStyle} />
                            <input
                                type="text" placeholder="Verification Code" value={code}
                                onChange={(e) => setCode(e.target.value)} style={inputStyle}
                                autoComplete="one-time-code"
                            />
                        </div>
                        <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
                            <Lock size={16} style={iconStyle} />
                            <input
                                type={showPassword ? "text" : "password"} placeholder="New Password"
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle}
                                autoComplete="new-password"
                            />
                        </div>
                        <button type="submit" disabled={isLoading} style={buttonStyle}>
                            {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Reset Password'}
                        </button>
                    </form>
                )}

                {view === 'force-change-password' && (
                    <form onSubmit={handleForceConfirm} style={{ width: '100%' }}>
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                            <Mail size={16} style={iconStyle} />
                            <input
                                type="email" placeholder="Organization Email" value={email}
                                readOnly style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                                autoComplete="username"
                            />
                        </div>
                        <div style={{ marginBottom: '1rem', position: 'relative' }}>
                            <Lock size={16} style={iconStyle} />
                            <input
                                type={showPassword ? "text" : "password"} placeholder="New Password"
                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle}
                                autoComplete="new-password"
                            />
                            <button
                                type="button" onClick={() => setShowPassword(!showPassword)}
                                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                            >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
                            <Lock size={16} style={iconStyle} />
                            <input
                                type={showPassword ? "text" : "password"} placeholder="Confirm Password"
                                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle}
                                autoComplete="new-password"
                            />
                        </div>
                        <button type="submit" disabled={isLoading} style={buttonStyle}>
                            {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Set Password and Login'}
                        </button>
                    </form>
                )}
            </div>

            <div style={{ position: 'absolute', bottom: '2rem', color: '#94a3b8', fontSize: '0.7rem' }}>
                RIS Dashboard v1.2
            </div>
        </div>
    );
};

export default LoginScreen;
